import React, { useEffect, useState, useMemo } from "react";
import {
Chart as ChartJS,
CategoryScale,
LinearScale,
PointElement,
LineElement,
Title,
Tooltip,
Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
Listbox,
ListboxButton,
ListboxOption,
ListboxOptions,
Combobox,
} from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { ReactComponent as VentasIcon } from '../../assets/icons/VentasIcon.svg';
import { ReactComponent as EstadisticasIcon } from '../../assets/icons/EstadisticasIcon.svg';
import { ReactComponent as TrofeoIcon } from '../../assets/icons/TrofeoIcon.svg';
import { ReactComponent as MiembrosIcon } from '../../assets/icons/MiembrosIcon.svg';
import { ReactComponent as EventosIcon } from '../../assets/icons/EventosIcon.svg';
import { ReactComponent as ClientesIcon } from '../../assets/icons/ClientesIcon.svg';
import { db } from "../../services/firebase";
import { useCotizaciones, aPesos } from "../../services/cotizaciones";
import LoadingSpinner from "../LoadingSpinner";
ChartJS.register(
CategoryScale,
LinearScale,
PointElement,
LineElement,
Title,
Tooltip,
Legend
);
const EstadisticasVentas = () => {
const [query, setQuery] = useState('');
const navigate = useNavigate(); 
const [ventas, setVentas] = useState([]);
const [clientes, setClientes] = useState([]);
const [tipoGraficoVentas, setTipoGraficoVentas] = useState("mensual");
const [tipoGraficoProducto, setTipoGraficoProducto] = useState("mensual");
const [dataChart, setDataChart] = useState({ labels: [], datasets: [] });
const [productos, setProductos] = useState([]);
const [isLoading, setIsLoading] = useState(true);
const [productoSeleccionado, setProductoSeleccionado] = useState("");
const [dataChartProducto, setDataChartProducto] = useState({
labels: [],
datasets: [],
});
const [monedaSeleccionada, setMonedaSeleccionada] = useState('ARS');
// Cotización del día para Dólares/Euros/Reales, compartida con EstadisticasCompras
// (antes cada pantalla hacía su propio fetch y solo sabía convertir Dólares).
const { cotizaciones } = useCotizaciones();
const dolarOficialVenta = cotizaciones?.Dólares ?? null;
const dolarCCLVenta = cotizaciones?.DólaresCCL ?? null;
const [topEventos, setTopEventos] = useState([]);
const formatNumber = (value) => {
if (value >= 1000000) {
return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
} else if (value >= 1000) {
return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
}
return value.toLocaleString();
};
const formatPrice = (value) => {
    const flooredValue = Math.floor(value);
    const locale = 'es-AR';
    const options = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return flooredValue.toLocaleString(locale, options);
};
const convertAmount = (amount) => {
const numericAmount = parseFloat(amount) || 0; 
if (monedaSeleccionada === 'DOLAR' && dolarOficialVenta) {
return numericAmount / dolarOficialVenta;
}
if (monedaSeleccionada === 'CCL' && dolarCCLVenta) {
return numericAmount / dolarCCLVenta;
}
return numericAmount;
};
const formatConvertedPrice = (value) => {
const convertedValue = convertAmount(value);
const prefix = monedaSeleccionada === 'ARS' ? '$' : 'US$';
const formattedPrice = formatPrice(convertedValue);
return `${prefix}${formattedPrice}`;
};
const formatConvertedNumberForChart = (value) => {
const convertedValue = convertAmount(value);
const prefix = monedaSeleccionada === 'ARS' ? '$' : 'US$';
let formattedValue;
if (convertedValue >= 1000000) {
formattedValue = (convertedValue / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
} else if (convertedValue >= 1000) {
formattedValue = (convertedValue / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
} else {
formattedValue = convertedValue.toLocaleString('es-AR', { maximumFractionDigits: 0 });
}
return `${prefix}${formattedValue}`;
}
useEffect(() => {
const fetchData = async () => {
try {
const [ventasSnapshot, clientesSnapshot, eventosSnapshot] =
await Promise.all([
getDocs(collection(db, "ventas")),
getDocs(collection(db, "clientes")),
getDocs(collection(db, "eventos")),
]);
const ventasData = ventasSnapshot.docs.map((doc) => ({
id: doc.id,
...doc.data(),
}));
setVentas(ventasData);
const eventosData = eventosSnapshot.docs.map((doc) => {
const data = doc.data();
const precio = parseFloat(data.precio) || 0;
const numInvitados = data.invitados ? data.invitados.length : 0;
const ingreso = precio * numInvitados;
return {
id: doc.id,
titulo: data.titulo,
ingreso: ingreso,
finished: data.finished,
};
});
const finishedEvents = eventosData.filter((evento) => evento.finished);
const sortedEventos = finishedEvents.sort((a, b) => b.ingreso - a.ingreso);
setTopEventos(sortedEventos.slice(0, 5));
const titulosEventosTerminados = new Set(
finishedEvents.map((e) => e.titulo)
);
const productosUnicos = new Set();
ventasData.forEach((venta) => {
    if (venta.tipoVenta === 'RESERVA' || venta.tipoVenta === 'EVENTO') {
        return;
    }
    venta.productosVendidos.forEach((p) => {
        const nombreProducto = p.nombreProducto;
        if (!titulosEventosTerminados.has(nombreProducto)) {
            productosUnicos.add(nombreProducto);
        }
    });
});
const productosArray = Array.from(productosUnicos);
setProductos(productosArray);
if (productosArray.length > 0)
setProductoSeleccionado(productosArray[0]);
// Cuentas de staff usadas para probar el sitio público como si fueran
// clientes (Martín Gallo, Tomás Sitta) — no son clientes reales, no deben
// contarse acá. Sacar cuando ya no hagan falta para pruebas.
const UIDS_CUENTAS_DE_PRUEBA = ['31464078', '42876938', '34853820', '36398917'];
const clientesData = clientesSnapshot.docs
.filter((doc) => !UIDS_CUENTAS_DE_PRUEBA.includes(doc.id))
.map((doc) => doc.data());
setClientes(clientesData);
setIsLoading(false);
} catch (error) {
console.error("Error fetching data:", error);
setIsLoading(false);
}
};
fetchData();
}, []);
useEffect(() => {
const hoy = new Date();
let labels = [];
let data = [];
const mesesMap = { bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
if (tipoGraficoVentas === "mensual") {
for (let i = 29; i >= 0; i--) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - i);
labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
data.push(0);
}
ventas.forEach((venta) => {
if (venta.tipoVenta === 'RESERVA' || venta.tipoVenta === 'EVENTO') {
return;
}
const fechaVenta = venta.fechaVenta instanceof Date 
? venta.fechaVenta 
: venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
for (let i = 0; i < 30; i++) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - (29 - i));
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth() &&
fechaVenta.getDate() === fecha.getDate()
) {
data[i] += aPesos(venta.totalFinal, venta.tipoDivisa, cotizaciones);
break;
}
}
});
} else if (tipoGraficoVentas === "semanal") {
for (let i = 6; i >= 0; i--) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - i);
labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
data.push(0);
}
ventas.forEach((venta) => {
const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
for (let i = 0; i < 7; i++) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - (6 - i));
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth() &&
fechaVenta.getDate() === fecha.getDate()
) {
data[i] += aPesos(venta.totalFinal, venta.tipoDivisa, cotizaciones);
break;
}
}
});
} else {
const cantidadMeses = mesesMap[tipoGraficoVentas] || 12;
for (let i = cantidadMeses - 1; i >= 0; i--) {
const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
labels.push(
fecha.toLocaleString("default", { month: "short", year: "numeric" })
);
data.push(0);
}
ventas.forEach((venta) => {
const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
for (let i = 0; i < labels.length; i++) {
const fecha = new Date(
hoy.getFullYear(),
hoy.getMonth() - (labels.length - 1 - i),
1
);
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth()
) {
data[i] += aPesos(venta.totalFinal, venta.tipoDivisa, cotizaciones);
break;
}
}
});
}
const convertedData = data.map(convertAmount);
setDataChart({
labels,
datasets: [
{
label: `Ventas (${tipoGraficoVentas})`,
data: convertedData,
borderColor: "#e53935",
backgroundColor: "rgba(255,0,0,0.2)",
borderWidth: 2,
tension: 0.01,
pointBackgroundColor: "#e53935",
pointBorderColor: "#e53935",
pointRadius: 1,
pointHoverRadius: 2,
pointStyle: "circle",
},
],
});
}, [ventas, tipoGraficoVentas, monedaSeleccionada, dolarOficialVenta, dolarCCLVenta, cotizaciones]);
useEffect(() => {
if (!productoSeleccionado) return;
const hoy = new Date();
let labels = [];
let data = [];
const mesesMap = { bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
if (tipoGraficoProducto === "mensual") {
for (let i = 29; i >= 0; i--) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - i);
labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
data.push(0);
}
ventas.forEach((venta) => {
const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
venta.productosVendidos.forEach((p) => {
if (p.nombreProducto === productoSeleccionado) {
for (let i = 0; i < 30; i++) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - (29 - i));
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth() &&
fechaVenta.getDate() === fecha.getDate()
) {
data[i] += aPesos(parseInt(p.cantidad) * parseFloat(p.precioUnidad), venta.tipoDivisa, cotizaciones);
break;
}
}
}
});
});
} else if (tipoGraficoProducto === "semanal") {
for (let i = 6; i >= 0; i--) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - i);
labels.push(`${fecha.getDate()}/${fecha.getMonth() + 1}`);
data.push(0);
}
ventas.forEach((venta) => {
const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
venta.productosVendidos.forEach((p) => {
if (p.nombreProducto === productoSeleccionado) {
for (let i = 0; i < 7; i++) {
const fecha = new Date(hoy);
fecha.setDate(hoy.getDate() - (6 - i));
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth() &&
fechaVenta.getDate() === fecha.getDate()
) {
data[i] += aPesos(parseInt(p.cantidad) * parseFloat(p.precioUnidad), venta.tipoDivisa, cotizaciones);
break;
}
}
}
});
});
} else {
const cantidadMeses = mesesMap[tipoGraficoProducto] || 12;
for (let i = cantidadMeses - 1; i >= 0; i--) {
const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
labels.push(
fecha.toLocaleString("default", { month: "short", year: "numeric" })
);
data.push(0);
}
ventas.forEach((venta) => {
const fechaVenta = venta.fechaVenta?.toDate ? venta.fechaVenta.toDate() : new Date(venta.fechaVenta);
venta.productosVendidos.forEach((p) => {
if (p.nombreProducto === productoSeleccionado) {
for (let i = 0; i < labels.length; i++) {
const fecha = new Date(
hoy.getFullYear(),
hoy.getMonth() - (labels.length - 1 - i),
1
);
if (
fechaVenta.getFullYear() === fecha.getFullYear() &&
fechaVenta.getMonth() === fecha.getMonth()
) {
data[i] += aPesos(parseInt(p.cantidad) * parseFloat(p.precioUnidad), venta.tipoDivisa, cotizaciones);
break;
}
}
}
});
});
}
const convertedData = data.map(convertAmount);
setDataChartProducto({
labels,
datasets: [
{
label: `Ventas (${tipoGraficoProducto})`,
data: convertedData,
borderColor: "#e53935",
backgroundColor: "rgba(255,0,0,0.2)",
borderWidth: 2,
tension: 0.01,
pointBackgroundColor: "#e53935",
pointBorderColor: "#e53935",
pointRadius: 1,
pointHoverRadius: 2,
pointStyle: "circle",
},
],
});
}, [ventas, productoSeleccionado, tipoGraficoProducto, monedaSeleccionada, dolarOficialVenta, dolarCCLVenta, cotizaciones]);
const totalVentasPeriodo =
dataChart.datasets[0]?.data.reduce((acc, val) => acc + val, 0) || 0;
const totalProductoPeriodo =
dataChartProducto.datasets[0]?.data.reduce((acc, val) => acc + val, 0) || 0;
const maxValue = dataChart.datasets[0]?.data.length
? Math.max(...dataChart.datasets[0].data)
: 0;
const magnitude =
maxValue > 0 ? Math.pow(10, Math.floor(Math.log10(maxValue))) : 1;
const roundedMax =
maxValue > 0 ? Math.ceil(maxValue / magnitude) * magnitude : undefined;
const maxValueProd = dataChartProducto.datasets[0]?.data.length
? Math.max(...dataChartProducto.datasets[0].data)
: 0;
const magnitudeProd =
maxValueProd > 0 ? Math.pow(10, Math.floor(Math.log10(maxValueProd))) : 1;
const roundedMaxProd =
maxValueProd > 0
? Math.ceil(maxValueProd / magnitudeProd) * magnitudeProd
: undefined;
const options = {
responsive: true,
plugins: {
tooltip: {
enabled: true,
backgroundColor: "#fff",
titleColor: "#000",
bodyColor: "#000",
borderColor: "#e53935",
borderWidth: 1,
cornerRadius: 12,
displayColors: false,
caretSize: 0,
padding: {
top: 3,
bottom: 3,
left: 10,
right: 10,
},
callbacks: {
title: () => null,
label: (context) => formatConvertedPrice(context.raw * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1)), 
},
},
legend: { display: false },
title: {
display: true,
text: `        Ventas totales del período: ${formatConvertedPrice(totalVentasPeriodo * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1))}`,
color: "black",
font: {
size: 14,
weight: "normal",
family: "Effra",
},
align: "start",
padding: { top: 20, bottom: 30 },
},
},
scales: {
y: {
beginAtZero: true,
grid: { display: false },
ticks: {
color: "black",
font: {
size: 11,
weight: "normal",
family: "Effra",
},
callback: function (value) {
return formatConvertedNumberForChart(value * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1));
},
},
},
x: {
grid: { display: false },
ticks: {
color: "black",
font: {
size: 11,
weight: "normal",
family: "Effra",
},
},
},
},
};
const optionsProducto = {
...options,
responsive: true,
plugins: {
tooltip: {
enabled: true,
backgroundColor: "#fff",
titleColor: "#000",
bodyColor: "#000",
borderColor: "#e53935",
borderWidth: 1,
cornerRadius: 12,
displayColors: false,
caretSize: 0,
padding: {
top: 3,
bottom: 3,
left: 10,
right: 10,
},
callbacks: {
title: () => null,
label: (context) => formatConvertedPrice(context.raw * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1)),
},
},
legend: { display: false },
title: {
display: true,
text: `        Ventas totales del período:  ${formatConvertedPrice(totalProductoPeriodo * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1))}`,
color: "black",
font: {
size: 14,
weight: "normal",
family: "Effra",
},
align: "start",
padding: { bottom: 30, top: 20 },
},
},
scales: {
y: {
beginAtZero: true,
grid: { display: false },
ticks: {
color: "black",
font: {
size: 11,
weight: "normal",
family: "Effra",
},
callback: function (value) {
return formatConvertedNumberForChart(value * (monedaSeleccionada === 'ARS' ? 1 : (monedaSeleccionada === 'DOLAR' ? dolarOficialVenta : dolarCCLVenta) || 1));
},
},
},
x: {
grid: { display: false },
ticks: {
color: "black",
font: {
size: 11,
weight: "normal",
family: "Effra",
},
},
},
},
};
const topProductos = useMemo(() => {
const totales = {};
ventas.forEach((venta) => {
if (venta.tipoVenta === 'RESERVA' || venta.tipoVenta === 'EVENTO') {
return;
}
venta.productosVendidos.forEach((p) => {
const monto = aPesos(parseFloat(p.cantidad) * parseFloat(p.precioUnidad), venta.tipoDivisa, cotizaciones);
if (totales[p.nombreProducto]) {
totales[p.nombreProducto] += monto;
} else {
totales[p.nombreProducto] = monto;
}
});
});
const sorted = Object.entries(totales)
.map(([nombre, total]) => ({ nombre, total }))
.sort((a, b) => b.total - a.total);
return sorted.slice(0, 5);
}, [ventas, cotizaciones]);
const topClientes = useMemo(() => {
    const totales = {};
    ventas.forEach((venta) => {
        if (venta.id && venta.id.startsWith('R-')) {
            return; 
        }
        const esVentaDeEvento = venta.productosVendidos && venta.productosVendidos.some(
            (p) => p.categoria === 'eventos'
        );
        if (esVentaDeEvento) {
            return; 
        }
        const clienteId = venta.cliente.numeroDeDocumento || venta.cliente.cuit || venta.cliente.id;
        const nombre = venta.cliente.nombre || venta.cliente.apellido
            ? `${venta.cliente.nombre} ${venta.cliente.apellido}`.trim()
            : venta.cliente.razonSocial || "Cliente Desconocido";
        const totalVentaEnPesos = aPesos(venta.totalFinal, venta.tipoDivisa, cotizaciones);
        if (totales[clienteId]) {
            totales[clienteId].total += totalVentaEnPesos;
        } else {
            totales[clienteId] = { nombre, total: totalVentaEnPesos, id: clienteId };
        }
    });
    const sorted = Object.values(totales).sort((a, b) => b.total - a.total);
    return sorted.slice(0, 5);
}, [ventas, cotizaciones]);
const topMiembros = useMemo(() => {
    const totales = {};
    clientes.forEach((cliente) => {
        const idConFormato = cliente.numeroDeDocumento || cliente.cuit;
        const clienteId = idConFormato || cliente.id;
        const nombre = cliente.nombre || cliente.apellido
            ? `${cliente.nombre} ${cliente.apellido}`.trim()
            : cliente.razonSocial || "Miembro Desconocido";
        let totalMiembro = parseFloat(cliente.montoHistorico) || 0;
        if (cliente.eventos && Array.isArray(cliente.eventos)) {
            cliente.eventos.forEach((evento) => {
                totalMiembro += parseFloat(evento.monto) || 0;
            });
        }
        totales[clienteId] = { id: clienteId, nombre, total: totalMiembro };
    });
    const sorted = Object.values(totales).sort((a, b) => b.total - a.total);
    return sorted.slice(0, 5);
}, [clientes])
const periodos = [
{ value: "semanal", label: "Semanales" },
{ value: "mensual", label: "Mensuales" },
{ value: "bimestral", label: "Bimestrales" },
{ value: "trimestral", label: "Trimestrales" },
{ value: "semestral", label: "Semestrales" },
{ value: "anual", label: "Anuales" },
];
const filteredProductos =
query === ""
? productos
: productos.filter((producto) => {
return producto.toLowerCase().includes(query.toLowerCase());
});
if (isLoading) return <LoadingSpinner />;
return (
<div
style={{ padding: '0 16px', position: 'relative', overflow: 'auto' }}
className="Trazabilidad"
>
<header
className="trazabilidad-header"
style={{
position: 'sticky',
top: '0',
backgroundColor: 'white',
zIndex: 10,
}}
>
<div
className="header-content"
style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
padding: '10px 0',
}}
>
<h1>Estadísticas</h1>
</div>
</header>
<div className="container" style={{ margin: '0 10px' }}>
<div className="formtitle-container">
<div className="formicon-container">
<VentasIcon
className="formicon"
style={{ height: '20px', margin: 'auto' }}
/>
</div>
<h1 className="formtitle">Ventas</h1>
</div>
<hr className="divider" style={{ marginBottom: '0' }} />
<div className="currency-selector-container">
{/* {dolarOficialVenta && (
<p className="exchange-rate-display">
1 U$D = ${formatPrice(dolarOficialVenta)} (Oficial)
</p>
)} */}
<span>Valores expresados en:</span>
<button
className={`currency-button ${monedaSeleccionada === 'ARS' ? 'active' : ''}`}
onClick={() => setMonedaSeleccionada('ARS')}
>ARS</button>
<button
className={`currency-button ${monedaSeleccionada === 'DOLAR' ? 'active' : ''}`}
onClick={() => setMonedaSeleccionada('DOLAR')}
disabled={!dolarOficialVenta}
>BNA</button>
<button
className={`currency-button ${monedaSeleccionada === 'CCL' ? 'active' : ''}`}
onClick={() => setMonedaSeleccionada('CCL')}
disabled={!dolarCCLVenta}
>CCL</button>
</div>
<div className="estadisticas-container">
<div className="estadisticas-charts-row">
<div className="chart-container chart-general">
<div
style={{
display: 'flex',
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'space-between',
borderBottom: '1px solid #e0e3e7',
padding: '0px 0 5px',
}}
>
<h3 className="chart-subtitle">
<EstadisticasIcon className="menu-icon" style={{ width: '12px' }} />
Gráfico de ventas
</h3>
<div className="input-group">
<Listbox value={tipoGraficoVentas} onChange={setTipoGraficoVentas}>
{({ open }) => (
<>
<ListboxButton
className={`custom-listbox-button ${open ? 'open' : ''}`}
style={{ minWidth: '140px', padding: '1px 7px 1px 15px' }}
>
{
periodos.find((p) => p.value === tipoGraficoVentas)
?.label
}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{periodos.map((periodo) => (
<ListboxOption
key={periodo.value}
value={periodo.value}
className="custom-listbox-option"
>
{periodo.label}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
</div>
<Line data={dataChart} options={options} className="line-chart" />
</div>
<div className="chart-container chart-producto">
<div
style={{
display: 'flex',
flexDirection: 'row',
alignItems: 'center',
justifyContent: 'space-between',
borderBottom: '1px solid #e0e3e7',
padding: '0px 0 5px',
}}
>
<h3 className="chart-subtitle">
<EstadisticasIcon className="menu-icon" style={{ width: '12px' }} />
Ventas por producto
</h3>
<div className="input-group">
<Combobox
value={productoSeleccionado}
onChange={(value) => {
setProductoSeleccionado(value);
setQuery('');
}}
>
{({ open }) => (
<>
<div style={{ position: 'relative', width: '100%' }}>
<Combobox.Button
as="div"
style={{ width: '92.75%' }}
className={`custom-listbox-button ${
open ? 'open' : ''
}`}
>
<Combobox.Input
className="custom-combobox-input"
placeholder={
productoSeleccionado || 'Selecciona un producto'
}
value={query}
onChange={(event) => setQuery(event.target.value)}
style={{
flexGrow: 1,
padding: '0',
border: 'none',
outline: 'none',
backgroundColor: 'transparent',
}}
/>
<span
className="custom-arrow-container"
style={{ flexShrink: 0 }}
>
<span className="custom-arrow"></span>
</span>
</Combobox.Button>
<Combobox.Options className="custom-listbox-options" style={{ maxHeight: '175px',minWidth:'207px'}}>
{productos.map((p) => (
<Combobox.Option
key={p}
value={p}
className="custom-listbox-option"
>
{p}
</Combobox.Option>
))}
</Combobox.Options>
</div>
</>
)}
</Combobox>
</div>
<div className="input-group">
<Listbox
value={tipoGraficoProducto}
onChange={setTipoGraficoProducto}
>
{({ open }) => (
<>
<ListboxButton
className={`custom-listbox-button ${open ? 'open' : ''}`}
style={{ minWidth: '140px', padding: '1px 7px 1px 15px' }}
>
{
periodos.find((p) => p.value === tipoGraficoProducto)
?.label
}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{periodos.map((periodo) => (
<ListboxOption
key={periodo.value}
value={periodo.value}
className="custom-listbox-option"
>
{periodo.label}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
</div>
<Line
data={dataChartProducto}
options={optionsProducto}
className="line-chart"
/>
</div>
</div>
<div className="top-lists-row" style={{ paddingBottom:'25px' }}>
<div className="top-list top-productos">
<div
style={{
display: 'flex',
flexDirection: 'row',
gap: '10px',
alignItems: 'center',
paddingBottom: '10px',
}}
>
<TrofeoIcon
className="formicon"
style={{ width: '17px', margin: '0', fill: '#2c2c2c' }}
/>
<h3 className="top-list-title">Top 5 Productos</h3>
</div>
<ol className="top-list-items">
{topProductos.map((p, index) => (
<li key={index} className="top-list-item">
<span className="item-value">
{formatConvertedPrice(p.total)}
</span>
<span className="item-name">{p.nombre}</span>
</li>
))}
</ol>
</div>
{}
<div className="top-list top-clientes">
<div
style={{
display: 'flex',
flexDirection: 'row',
gap: '10px',
alignItems: 'center',
paddingBottom: '10px',
}}
>
<ClientesIcon
className="formicon"
style={{ width: '14px', margin: '0', fill: '#2c2c2c' }}
/>
<h3 className="top-list-title">Top 5 Clientes</h3>
</div>
<ol className="top-list-items">
{topClientes.map((c, index) => (
  <li
    key={index}
    className="top-list-item"
    style={{ cursor: "pointer" }}
    onClick={() => window.open(`/clientes/${c.id}`, "_blank")} 
  >
    <span className="item-value">
      {formatConvertedPrice(c.total)}
    </span>
    <span className="item-name">{c.nombre}</span>
  </li>
))}
</ol>
</div>
<div className="top-list top-eventos">
<div
style={{
display: 'flex',
flexDirection: 'row',
gap: '10px',
alignItems: 'center',
paddingBottom: '10px',
}}
>
<EventosIcon
className="formicon"
style={{ width: '16px', margin: '0', fill: '#2c2c2c' }}
/>
<h3 className="top-list-title">Top 5 Eventos</h3>
</div>
<ol className="top-list-items">
{topEventos.map((evento) => (
<li key={evento.id} className="top-list-item" onClick={() => window.open(`/eventos/${evento.id}`, "_blank")}>
<span className="item-value">
{formatConvertedPrice(evento.ingreso)}
</span>
<span className="item-name">{evento.titulo}</span>
</li>
))}
</ol>
</div>
{}
<div className="top-list top-miembros">
<div
style={{
display: 'flex',
flexDirection: 'row',
gap: '10px',
alignItems: 'center',
paddingBottom: '10px',
}}
>
<MiembrosIcon
className="formicon"
style={{ width: '16px', margin: '0', fill: '#2c2c2c' }}
/>
<h3 className="top-list-title">Top 5 Miembros</h3>
</div>
<ol className="top-list-items">
  {topMiembros.map((miembro, index) => (
    <li
      key={index}
      className="top-list-item"
      style={{ cursor: "pointer" }}
      onClick={() => window.open(`/clientes/${miembro.id}`, "_blank")}
    >
      <span className="item-value">{formatConvertedPrice(miembro.total)}</span>
      <span className="item-name">{miembro.nombre}</span>
    </li>
  ))}
</ol>
</div>
</div>
</div>
</div>
</div>
);
};
export default EstadisticasVentas;
